import { MouseEvent, ReactNode } from 'react';
import { handleCardContactEvent } from '../../../helpers/cards';
import { EntityTypes } from '../../../new-lib/constants/common';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getPhoneNumber } from '../../../helpers/common';
import WhatsappBuilder from '../../../helpers/whatsapp/whatsapp-builder';
import { useRouter } from 'next/router';
import { phoneHelper } from '../../../helpers/phone-helper';
import { trackEvent } from '../../../new-lib/remarketing';
import { useTranslation } from 'next-i18next';
import WhatsappIcon from '../../svg-icons/WhatsappIcon';

interface CardCTAProps {
  entity: any;
  entityType: EntityTypes;
  clickEventData: any;
}

interface ButtonProps {
  text: string;
  styles: object;
  handleClick: (e: MouseEvent) => void;
  dataTest: string;
  icon?: ReactNode | JSX.Element;
}

const Button = ({ icon, text, styles, handleClick, dataTest }: ButtonProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);

  return (
    <ButtonContainer data-test={dataTest} onClick={handleClick} style={styles}>
      {icon}
      {isMobile && text}
    </ButtonContainer>
  );
};

const CallToAction = ({ entity, entityType, clickEventData }: CardCTAProps) => {
  const { t } = useTranslation('common');

  const isMobile = useSelector((state: any) => state.app.isMobile);

  let { asPath } = useRouter();
  const referer = useSelector((state: any) => state.app.referer);
  const userId = useSelector((state: any) => state.app.userId);
  const eventId = useSelector((state: any) => state.app.eventId);
  const PHONE_NUMBER = getPhoneNumber(referer);
  const whatsapp = new WhatsappBuilder(
    entity,
    asPath,
    referer,
    userId,
    eventId,
    entityType as any
  );

  const handleWhatsAppClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    whatsapp.launchWhatsApp();

    handleCardContactEvent({
      ...{
        ...clickEventData,
        area_id: entity?.area?.id || entity?.area_id,
        area_name: entity?.area?.name
      },
      eventAction: 'whatsapp',
      eventId: eventId,
      entityType: entityType
    });
    trackEvent.purchase({
      listingIds: [entity.id],
      listingPrice: entity.min_price
    });
  };
  const handlePhoneCall = (e: MouseEvent) => {
    e.stopPropagation();
    phoneHelper(entity, asPath, Number(PHONE_NUMBER), entityType as any);
    handleCardContactEvent({
      ...clickEventData,
      eventAction: 'call',
      eventId: eventId,
      entityType: entityType
    });
    trackEvent.purchase({
      listingIds: [entity.id],
      listingPrice: entity.min_price
    });
  };

  const router = useRouter();

  return (
    <Container>
      {isMobile && (
        <Button
          handleClick={(e: MouseEvent) => {
            e.preventDefault();
            handlePhoneCall(e);
            router.push(`tel:+2${PHONE_NUMBER}`);
          }}
          text={t('call_us')}
          styles={{
            ...(isMobile && { width: '140px' }),
            background: '#FBFCFE',
            border: '1px solid #C6D0DE',
            color: '#1A2028'
          }}
          dataTest={`call-${PHONE_NUMBER}`}
        />
      )}

      <Button
        text={t('whatsapp')}
        styles={{
          width: '100%',
          background: '#4CD964',
          ...(isMobile && { border: '1px solid transparent' }),
          color: '#fff'
        }}
        icon={<WhatsappIcon />}
        handleClick={(event: MouseEvent) => handleWhatsAppClick(event)}
        dataTest={`whatsapp-btn`}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    width: 100%;
  }
`;

const ButtonContainer = styled.button`
  width: 40px;
  height: 40px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: materialMedium;
  svg {
    width: 24px;
    height: 24px;
  }
  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    width: 100% !important;
    gap: 8px;
  }
`;
export default CallToAction;
